import { useState, useEffect } from 'react';
import VerifikatorLayout from '../../components/VerifikatorLayout';
import PreviewSurat from '../../components/PreviewSurat';
import api from '../../services/api';
import { 
  FiInbox, 
  FiUser, 
  FiCalendar, 
  FiCheckCircle, 
  FiXCircle, 
  FiAlertCircle,
  FiFileText,
  FiChevronRight,
  FiFilter,
  FiClock,
  FiEye
} from 'react-icons/fi';

const SuratMasuk = () => {
  const [suratList, setSuratList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('semua');
  const [selectedSurat, setSelectedSurat] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showViewOnlyModal, setShowViewOnlyModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [keterangan, setKeterangan] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [suratPengantarFile, setSuratPengantarFile] = useState(null);

  useEffect(() => {
    fetchSuratMasuk();
  }, []);

  const fetchSuratMasuk = async () => {
    try {
      setLoading(true);
      const response = await api.get('/verifikator/surat-masuk');
      if (response.data.success) {
        setSuratList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching surat:', error);
      showToast('Gagal mengambil data surat', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleViewDetail = (surat) => {
    setSelectedSurat(surat);
    const canVerify = surat.status_surat === 'menunggu_verifikasi_rt' || 
                      surat.status_surat === 'menunggu_verifikasi_rw';
    
    if (canVerify) {
      setShowDetailModal(true);
    } else {
      setShowViewOnlyModal(true);
    }
  };

  const handleApprove = async () => {
    if (!selectedSurat) return;

    if (!suratPengantarFile) {
      showToast('Surat pengantar harus diupload!', 'error');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('surat_pengantar', suratPengantarFile);
      formData.append('keterangan', keterangan || 'Disetujui');

      await api.put(`/verifikator/surat/${selectedSurat.id}/approve`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      showToast('Surat berhasil disetujui!', 'success');
      setShowApproveModal(false);
      setShowDetailModal(false);
      setKeterangan('');
      setSuratPengantarFile(null);
      
      // Remove approved surat from list
      setSuratList(prev => prev.filter(s => s.id !== selectedSurat.id));
      setSelectedSurat(null);
    } catch (err) {
      console.error('Error approving surat:', err);
      showToast(err.response?.data?.message || 'Gagal menyetujui surat', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSurat) return;

    if (!keterangan.trim()) {
      showToast('Keterangan penolakan harus diisi', 'error');
      return;
    }

    try {
      setSubmitting(true);
      await api.put(`/verifikator/surat/${selectedSurat.id}/reject`, {
        keterangan
      });
      
      showToast('Surat berhasil ditolak', 'success');
      setShowRejectModal(false);
      setShowDetailModal(false);
      setKeterangan('');
      
      // Remove rejected surat from list
      setSuratList(prev => prev.filter(s => s.id !== selectedSurat.id));
      setSelectedSurat(null);
    } catch (err) {
      console.error('Error rejecting surat:', err);
      showToast(err.response?.data?.message || 'Gagal menolak surat', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'menunggu_verifikasi_rt': { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', text: 'Menunggu RT', icon: FiClock },
      'menunggu_verifikasi_rw': { color: 'bg-blue-50 text-blue-700 border-blue-200', text: 'Menunggu RW', icon: FiClock },
      'disetujui_rt': { color: 'bg-green-50 text-green-700 border-green-200', text: 'Disetujui RT', icon: FiCheckCircle },
      'disetujui_rw': { color: 'bg-green-50 text-green-700 border-green-200', text: 'Disetujui RW', icon: FiCheckCircle },
      'ditolak_rt': { color: 'bg-red-50 text-red-700 border-red-200', text: 'Ditolak RT', icon: FiXCircle },
      'ditolak_rw': { color: 'bg-red-50 text-red-700 border-red-200', text: 'Ditolak RW', icon: FiXCircle },
      'ditolak': { color: 'bg-red-50 text-red-700 border-red-200', text: 'Ditolak', icon: FiXCircle },
      'menunggu_admin': { color: 'bg-purple-50 text-purple-700 border-purple-200', text: 'Menunggu Admin', icon: FiClock },
      'selesai': { color: 'bg-green-50 text-green-700 border-green-200', text: 'Selesai', icon: FiCheckCircle },
    };
    const config = statusConfig[status] || { color: 'bg-gray-50 text-gray-700 border-gray-200', text: status, icon: FiAlertCircle };
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
        <Icon size={12} />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffHours < 1) {
      const diffMins = Math.floor(diffTime / (1000 * 60));
      return `${diffMins} menit lalu`;
    }
    if (diffHours < 24) return `${diffHours} jam lalu`;
    if (diffDays === 0) return 'Hari ini';
    if (diffDays === 1) return 'Kemarin';
    if (diffDays < 7) return `${diffDays} hari lalu`;
    
    return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredSurat = selectedStatus === 'semua' 
    ? suratList 
    : suratList.filter(s => s.status_surat === selectedStatus);

  const stats = {
    semua: suratList.length,
    menunggu: suratList.filter(s => s.status_surat?.includes('menunggu_verifikasi')).length,
    disetujui: suratList.filter(s => s.status_surat?.includes('disetujui')).length,
    ditolak: suratList.filter(s => s.status_surat?.includes('ditolak')).length
  };

  if (loading) {
    return (
      <VerifikatorLayout>
        <div className="p-4 space-y-4">
          <div className="animate-pulse">
            <div className="h-20 bg-gray-200 rounded-xl mb-3"></div>
            <div className="h-24 bg-gray-200 rounded-xl mb-3"></div>
            <div className="h-24 bg-gray-200 rounded-xl mb-3"></div>
          </div>
        </div>
      </VerifikatorLayout>
    );
  }

  return (
    <VerifikatorLayout>
      <div className="bg-gradient-to-b from-slate-50 to-white min-h-screen pb-4">
        
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-700 to-slate-900 text-white px-4 py-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <FiInbox size={24} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Surat Masuk</h1>
              <p className="text-slate-300 text-sm">{stats.semua} total surat</p>
            </div>
          </div>

          {/* Stats Mini */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center border border-white/20">
              <p className="text-yellow-400 text-lg font-bold">{stats.menunggu}</p>
              <p className="text-xs text-slate-300">Menunggu</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center border border-white/20">
              <p className="text-green-400 text-lg font-bold">{stats.disetujui}</p>
              <p className="text-xs text-slate-300">Disetujui</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center border border-white/20">
              <p className="text-red-400 text-lg font-bold">{stats.ditolak}</p>
              <p className="text-xs text-slate-300">Ditolak</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="px-4 py-3 bg-white border-b border-slate-200 sticky top-16 z-10 shadow-sm">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <FiFilter className="text-slate-600 flex-shrink-0" size={18} />
            {[
              { key: 'semua', label: 'Semua', count: stats.semua },
              { key: 'menunggu_verifikasi_rt', label: 'Menunggu RT', count: suratList.filter(s => s.status_surat === 'menunggu_verifikasi_rt').length },
              { key: 'menunggu_verifikasi_rw', label: 'Menunggu RW', count: suratList.filter(s => s.status_surat === 'menunggu_verifikasi_rw').length },
            ].map((filter) => (
              <button
                key={filter.key}
                onClick={() => setSelectedStatus(filter.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  selectedStatus === filter.key
                    ? 'bg-slate-800 text-white shadow-md'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {filter.label} {filter.count > 0 && `(${filter.count})`}
              </button>
            ))}
          </div>
        </div>

        {/* Surat List */}
        <div className="px-4 py-4 space-y-3">
          {filteredSurat.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-100">
              <FiAlertCircle className="mx-auto text-gray-300 mb-3" size={48} />
              <p className="text-gray-500 text-sm font-medium">Tidak ada surat {selectedStatus !== 'semua' ? `dengan status ${selectedStatus}` : ''}</p>
            </div>
          ) : (
            filteredSurat.map((surat) => {
              const canVerify = surat.status_surat === 'menunggu_verifikasi_rt' || 
                               surat.status_surat === 'menunggu_verifikasi_rw';
              
              return (
                <div
                  key={surat.id}
                  onClick={() => handleViewDetail(surat)}
                  className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm hover:shadow-md transition-all active:scale-98 cursor-pointer"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center flex-shrink-0">
                      <FiFileText className="text-slate-700" size={20} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-sm mb-1 truncate">
                        {surat.nama_surat || 'Surat'}
                      </h3>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        <FiUser size={12} />
                        <span className="truncate">{surat.nama_pemohon || 'Warga'}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs text-gray-400 mb-2">
                        <FiCalendar size={12} />
                        <span>{formatDate(surat.created_at)}</span>
                      </div>

                      <div className="mt-2">
                        {getStatusBadge(surat.status_surat)}
                      </div>
                    </div>

                    <FiChevronRight className="text-gray-400 flex-shrink-0 mt-2" size={20} />
                  </div>

                  {/* Quick Actions for Pending */}
                  {canVerify && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedSurat(surat);
                          setShowDetailModal(true);
                        }}
                        className="flex-1 py-2 px-3 bg-slate-700 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-slate-800 active:scale-95 transition-all"
                      >
                        <FiEye size={14} />
                        Detail & Verifikasi
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Toast Notification */}
        {toastMessage && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
            <div className={`px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 ${
              toastType === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
            }`}>
              {toastType === 'success' ? <FiCheckCircle size={18} /> : <FiXCircle size={18} />}
              <span className="font-semibold">{toastMessage}</span>
            </div>
          </div>
        )}

        {/* Detail Modal - For Verification */}
        {showDetailModal && selectedSurat && (
          <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-end sm:items-center justify-center min-h-screen px-4 pt-4 pb-4">
              <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity backdrop-blur-sm" onClick={() => setShowDetailModal(false)}></div>

              <div className="inline-block w-full max-w-2xl bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all relative">
                {/* Header */}
                <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-4 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">Detail Surat Pengajuan</h3>
                      <p className="mt-1 text-sm text-slate-200">{selectedSurat.nama_surat}</p>
                    </div>
                    <button
                      onClick={() => setShowDetailModal(false)}
                      className="ml-3 text-slate-300 hover:text-white transition-colors p-1"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="bg-white px-4 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    {getStatusBadge(selectedSurat.status_surat)}
                  </div>

                  {/* Data Pemohon */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <FiUser size={18} />
                      Data Pemohon
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-gray-600 text-xs">Nama:</span>
                        <p className="font-semibold text-gray-900 mt-1">{selectedSurat.nama_pemohon}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-gray-600 text-xs">NIK:</span>
                        <p className="font-semibold text-gray-900 mt-1">{selectedSurat.nik_pemohon}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-gray-600 text-xs">RT/RW:</span>
                        <p className="font-semibold text-gray-900 mt-1">{selectedSurat.pemohon_rt}/{selectedSurat.pemohon_rw}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-gray-600 text-xs">Tanggal:</span>
                        <p className="font-semibold text-gray-900 mt-1">{new Date(selectedSurat.created_at).toLocaleDateString('id-ID')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Data Surat Content */}
                  {(() => {
                    try {
                      const dataSurat = typeof selectedSurat.data_surat === 'string' 
                        ? JSON.parse(selectedSurat.data_surat || '{}') 
                        : selectedSurat.data_surat || {};
                      const entries = Object.entries(dataSurat).filter(([key]) => !key.startsWith('_'));
                      
                      if (entries.length > 0) {
                        return (
                          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                            <h4 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                              <FiFileText size={18} />
                              Isi Data Surat
                            </h4>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              {entries.map(([key, value]) => (
                                <div key={key} className="bg-white rounded-lg p-3 border border-green-100 col-span-2">
                                  <span className="text-gray-600 text-xs font-medium capitalize">{key.replace(/_/g, ' ')}:</span>
                                  <p className="font-semibold text-gray-900 mt-1 break-words">{value || '-'}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    } catch (e) {
                      console.error('Error parsing data_surat:', e);
                      return null;
                    }
                  })()}

                  {/* Preview Button */}
                  <button
                    onClick={() => setShowPreview(true)}
                    className="w-full inline-flex justify-center items-center px-4 py-3 border-2 border-slate-300 rounded-xl text-sm font-semibold text-slate-700 bg-white hover:bg-slate-50 transition-all"
                  >
                    <FiEye className="mr-2" size={18} />
                    Lihat Preview Surat Lengkap
                  </button>
                </div>

                {/* Actions */}
                <div className="bg-gray-50 px-4 py-4 flex flex-col gap-2 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowApproveModal(true);
                    }}
                    className="w-full inline-flex justify-center items-center px-6 py-3 rounded-xl text-sm font-semibold text-white bg-green-600 hover:bg-green-700 transition-all shadow-lg"
                  >
                    <FiCheckCircle className="mr-2" size={18} />
                    Setujui Surat
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setShowRejectModal(true);
                    }}
                    className="w-full inline-flex justify-center items-center px-6 py-3 rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-all shadow-lg"
                  >
                    <FiXCircle className="mr-2" size={18} />
                    Tolak Surat
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="w-full inline-flex justify-center items-center px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all"
                  >
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approve Modal */}
        {showApproveModal && selectedSurat && (
          <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-4">
              <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity backdrop-blur-sm" onClick={() => setShowApproveModal(false)}></div>

              <div className="inline-block w-full max-w-lg bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all relative">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                      <FiCheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">Setujui Surat</h3>
                      <p className="mt-2 text-sm text-gray-500">
                        Anda akan menyetujui surat <strong>{selectedSurat.nama_surat}</strong> dari <strong>{selectedSurat.nama_pemohon}</strong>.
                      </p>
                      
                      {/* Upload Surat Pengantar */}
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Upload Surat Pengantar <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => setSuratPengantarFile(e.target.files[0])}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                        />
                        {suratPengantarFile && (
                          <p className="mt-2 text-xs text-green-600">
                            ✓ File terpilih: {suratPengantarFile.name}
                          </p>
                        )}
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Keterangan (opsional)
                        </label>
                        <textarea
                          value={keterangan}
                          onChange={(e) => setKeterangan(e.target.value)}
                          rows={3}
                          className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-green-500 focus:border-green-500 text-sm"
                          placeholder="Catatan persetujuan..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 flex flex-col-reverse sm:flex-row-reverse gap-2">
                  <button
                    onClick={handleApprove}
                    disabled={submitting || !suratPengantarFile}
                    className="w-full inline-flex justify-center rounded-xl px-4 py-2.5 bg-green-600 text-sm font-semibold text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Memproses...' : 'Ya, Setujui'}
                  </button>
                  <button
                    onClick={() => {
                      setShowApproveModal(false);
                      setSuratPengantarFile(null);
                    }}
                    disabled={submitting}
                    className="w-full inline-flex justify-center rounded-xl px-4 py-2.5 border-2 border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Batal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && selectedSurat && (
          <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 py-4">
              <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity backdrop-blur-sm" onClick={() => setShowRejectModal(false)}></div>

              <div className="inline-block w-full max-w-lg bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all relative">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                      <FiXCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">Tolak Surat</h3>
                      <p className="mt-2 text-sm text-gray-500">
                        Anda akan menolak surat <strong>{selectedSurat.nama_surat}</strong> dari <strong>{selectedSurat.nama_pemohon}</strong>.
                      </p>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Alasan Penolakan <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={keterangan}
                          onChange={(e) => setKeterangan(e.target.value)}
                          rows={4}
                          className="block w-full border border-gray-300 rounded-lg shadow-sm py-2 px-3 focus:ring-red-500 focus:border-red-500 text-sm"
                          placeholder="Jelaskan alasan penolakan..."
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 flex flex-col-reverse sm:flex-row-reverse gap-2">
                  <button
                    onClick={handleReject}
                    disabled={submitting || !keterangan.trim()}
                    className="w-full inline-flex justify-center rounded-xl px-4 py-2.5 bg-red-600 text-sm font-semibold text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Memproses...' : 'Ya, Tolak'}
                  </button>
                  <button
                    onClick={() => setShowRejectModal(false)}
                    disabled={submitting}
                    className="w-full inline-flex justify-center rounded-xl px-4 py-2.5 border-2 border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Batal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View-Only Detail Modal */}
        {showViewOnlyModal && selectedSurat && (
          <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-end sm:items-center justify-center min-h-screen px-4 pt-4 pb-4">
              <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity backdrop-blur-sm" onClick={() => setShowViewOnlyModal(false)}></div>

              <div className="inline-block w-full max-w-2xl bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all relative">
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-4 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white">Detail Surat</h3>
                      <p className="mt-1 text-sm text-blue-100">{selectedSurat.nama_surat}</p>
                    </div>
                    <button
                      onClick={() => setShowViewOnlyModal(false)}
                      className="ml-3 text-blue-200 hover:text-white transition-colors p-1"
                    >
                      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="bg-white px-4 py-5 space-y-4 max-h-[60vh] overflow-y-auto">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    {getStatusBadge(selectedSurat.status_surat)}
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                      <FiUser size={18} />
                      Data Pemohon
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white rounded-lg p-3 border border-blue-100 col-span-2">
                        <span className="text-gray-600 text-xs">Nama:</span>
                        <p className="font-semibold text-gray-900 mt-1">{selectedSurat.nama_pemohon}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-gray-600 text-xs">NIK:</span>
                        <p className="font-semibold text-gray-900 mt-1">{selectedSurat.nik_pemohon}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <span className="text-gray-600 text-xs">RT/RW:</span>
                        <p className="font-semibold text-gray-900 mt-1">{selectedSurat.pemohon_rt}/{selectedSurat.pemohon_rw}</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setShowViewOnlyModal(false);
                      setShowPreview(true);
                    }}
                    className="w-full inline-flex justify-center items-center px-4 py-3 border-2 border-blue-300 rounded-xl text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all"
                  >
                    <FiEye className="mr-2" size={18} />
                    Lihat Preview Surat
                  </button>
                </div>

                <div className="bg-gray-50 px-4 py-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowViewOnlyModal(false)}
                    className="w-full inline-flex justify-center items-center px-6 py-3 border-2 border-gray-300 rounded-xl text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-all"
                  >
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && selectedSurat && (
          <PreviewSurat
            surat={{
              ...selectedSurat,
              jenis_surat: {
                nama_surat: selectedSurat.nama_surat,
                kode_surat: selectedSurat.kode_surat,
                fields: selectedSurat.fields,
                template_konten: selectedSurat.template_konten,
                kalimat_pembuka: selectedSurat.kalimat_pembuka
              }
            }}
            onClose={() => setShowPreview(false)}
          />
        )}
      </div>
    </VerifikatorLayout>
  );
};

export default SuratMasuk;
